/**
 * SetupWizard — Phase 24 / Task 24.8 (DEC-099). Full-screen 8-step onboarding.
 *
 * Rendered by App.tsx BEFORE the routed shell when a new hauler is gated (not
 * all_complete AND not banner_dismissed, for TENANT_ADMIN / DISPATCH_MANAGER).
 * "Save & Continue" marks the current section complete; "Skip for now" advances
 * without marking; "Launch lodeX Pro" (review step) marks all_complete + exits.
 *
 * Hybrid §75 model: section completion is explicit (set on Continue). Steps 2/3
 * reuse the Task 24.3 tabs; 4/5/6 reuse Phase 9 fleet hooks; 7 is read-only
 * (per-tenant rate/destination config deferred); driver-invite deferred.
 */
import * as React from 'react';
import { useUserProfile } from '../lib/use-user-timezone';
import {
  WIZARD_STEPS,
  useWizardStatus,
  useUpdateWizardStatus,
} from '../features/setup-wizard/hooks';
import {
  CompanyInfoStep,
  OperatorsStep,
  DisposalStep,
  YardsStep,
  FleetStep,
  DriversStep,
  JobTypesStep,
  ReviewStep,
  type StepProps,
} from '../features/setup-wizard/steps';
import { MANAGE_ROLES } from '../lib/role-gates';

const TENANT_ADMIN_ROLES = new Set(['TENANT_ADMIN', 'PLATFORM_ADMIN']);

export interface SetupWizardProps {
  /** Called after Launch (all_complete) or a permanent skip — host re-checks the gate. */
  readonly onExit: () => void;
}

function renderStep(step: number, props: StepProps): React.JSX.Element {
  switch (step) {
    case 1:
      return <CompanyInfoStep {...props} />;
    case 2:
      return <OperatorsStep {...props} />;
    case 3:
      return <DisposalStep {...props} />;
    case 4:
      return <YardsStep {...props} />;
    case 5:
      return <FleetStep {...props} />;
    case 6:
      return <DriversStep {...props} />;
    case 7:
      return <JobTypesStep />;
    default:
      return <></>;
  }
}

export function SetupWizard({ onExit }: SetupWizardProps): React.JSX.Element {
  const { data: profile } = useUserProfile();
  const role = profile?.profile.role ?? '';
  const stepProps: StepProps = {
    canManage: MANAGE_ROLES.has(role),
    isTenantAdmin: TENANT_ADMIN_ROLES.has(role),
  };

  const { data: status } = useWizardStatus();
  const update = useUpdateWizardStatus();
  const [step, setStep] = React.useState(1);

  // Resume at the last-viewed section once status loads.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (!seeded.current && status !== undefined) {
      seeded.current = true;
      setStep(Math.min(Math.max(status.lastSectionViewed, 1), 8));
    }
  }, [status]);

  const goTo = (next: number): void => {
    setStep(next);
    update.mutate({ lastSectionViewed: next });
  };

  const onContinue = (): void => {
    if (step <= 7) {
      update.mutate({ section: step, complete: true, lastSectionViewed: step + 1 });
      setStep(step + 1);
    }
  };
  const onSkip = (): void => {
    if (step <= 7) goTo(step + 1);
  };
  const onLaunch = (): void => {
    update.mutate({ allComplete: true }, { onSuccess: () => onExit() });
  };
  // "Skip setup for now" — release the gate (banner-dismissed); resumable later
  // from Settings → Getting started.
  const onSkipWizard = (): void => {
    update.mutate({ bannerDismissed: true }, { onSuccess: () => onExit() });
  };

  const current = WIZARD_STEPS[step - 1];

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
        padding: '32px 20px',
        overflowY: 'auto',
      }}
    >
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        {/* Header + progress */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            Welcome to lodeX Pro
          </h1>
          <button
            type="button"
            onClick={onSkipWizard}
            disabled={update.isPending}
            style={{
              fontSize: '13px',
              color: 'var(--color-text-tertiary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Skip setup for now →
          </button>
        </div>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
          Let&apos;s get you set up. Step {step} of 8 — {current?.title}
        </p>
        <div
          style={{
            height: '6px',
            backgroundColor: 'var(--color-bg-tertiary)',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '8px',
          }}
        >
          <div
            style={{
              width: `${(step / 8) * 100}%`,
              height: '100%',
              backgroundColor: 'var(--color-accent-blue)',
              transition: 'width 0.25s ease',
            }}
          />
        </div>
        {/* Step pips */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {WIZARD_STEPS.map((s) => {
            const done = s.n <= 7 && status?.sectionsComplete[s.n - 1] === true;
            const active = s.n === step;
            return (
              <button
                key={s.n}
                type="button"
                onClick={(): void => goTo(s.n)}
                style={{
                  fontSize: '11px',
                  padding: '3px 8px',
                  borderRadius: 'var(--radius-pill)',
                  border: '1px solid var(--color-border-secondary)',
                  cursor: 'pointer',
                  backgroundColor: active ? 'var(--color-accent-blue)' : 'transparent',
                  color: active
                    ? 'var(--color-on-accent)'
                    : done
                      ? 'var(--color-success)'
                      : 'var(--color-text-secondary)',
                }}
              >
                {done ? '✓ ' : ''}
                {s.n}. {s.title}
              </button>
            );
          })}
        </div>

        {/* Step body */}
        <div
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-card)',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          {step === 8 ? (
            <ReviewStep
              status={status?.sectionsComplete ?? []}
              onLaunch={onLaunch}
              launching={update.isPending}
            />
          ) : (
            renderStep(step, stepProps)
          )}
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            disabled={step === 1}
            onClick={(): void => goTo(step - 1)}
            style={{
              fontSize: '14px',
              color: 'var(--color-text-secondary)',
              background: 'none',
              border: 'none',
              cursor: step === 1 ? 'default' : 'pointer',
              opacity: step === 1 ? 0.4 : 1,
            }}
          >
            ‹ Back
          </button>
          {step <= 7 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                type="button"
                onClick={onSkip}
                style={{
                  fontSize: '13px',
                  color: 'var(--color-text-tertiary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={onContinue}
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-on-accent)',
                  backgroundColor: 'var(--color-accent-blue)',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  padding: '8px 18px',
                  cursor: 'pointer',
                }}
              >
                Save &amp; Continue ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
